// AddAssetDialog — adds an asset type to a capture space (buildspec
// 20-ui §5.7, PRD §7.3.3). Three modes share one dialog:
//
//   1. Search (default): debounced 250 ms text input hitting
//      `GET /asset-types/search?q=`. Results group by System →
//      SubSystem → Type and are tappable. Already-present asset
//      types are filtered out client-side so field staff cannot
//      double-add a row.
//   2. Pick category (only via "Create new asset" CTA when search
//      returns zero matches): renders a SubSystemPicker so the
//      field-staff user chooses the parent CCL sub-system before
//      the form opens. Required because the backend endpoint
//      (`POST /capture/field-additions`) refuses to create an
//      asset without a parent sub-system (PRD §6.5).
//   3. Create variant: a "Can't find it? Create variant" footer
//      pill (when results exist) and the empty-state "+ Create new
//      asset" CTA both surface C3.7's `<CreateVariantForm>` via the
//      `renderCreateVariantForm` render prop. The footer-pill path
//      anchors to the first visible result's sub-system; the
//      empty-state path passes through the picker first. When the
//      prop is omitted the create UI is hidden entirely.
//
// On select, the dialog dispatches a `createItem` mutation through
// the capture-client seam so the Phase-1/2 → Phase-3 adapter swap
// is opaque to callers. Superseded searches abort via
// `useAbortableEffect` so a fast typist's late response cannot
// overwrite a newer render. Server errors surface inline so the
// user can retry without losing their query.
//
// Visual contract: bare-variant Dialog (focus-trap + portal +
// overlay only) wrapped in a Citycare-branded card. The header
// shows "Add asset" as a display-typeface title with a contextual
// sub-line ("Find an asset type to add to <spaceName>") so field
// staff always see which space they are editing.

import { useCallback, useState, type ReactElement, type ReactNode } from 'react'

import { ApiError, isAbortError } from '../../hooks/api'
import { useAbortableEffect } from '../../hooks/use-abortable-effect'
import {
  ASSET_TYPE_SEARCH_MIN_QUERY,
  type AssetTypeSearchResponse,
} from '../../hooks/use-classification'
import { Alert } from '../../ui/alert'
import { Dialog } from '../../ui/dialog'

import { defaultSearch } from './default-asset-type-search'
import type { CaptureClient } from '../client/capture-client.types'

import type {
  AssetSubSystemListRow,
  AssetSystemListRow,
} from '../../hooks/use-classification'

import {
  AddAssetSearchResults,
  groupBySystem,
  type SearchState,
} from './add-asset-search-results'
import { classifyByQuery } from './asset-search-classify'
import { SubSystemPicker, type SubSystemPick } from './sub-system-picker'

const SEARCH_DEBOUNCE_MS = 250

export interface CreateVariantFormRenderProps {
  subSystemId: string
  subSystemName: string
  // Parent system name — used by the variant form's breadcrumb pill
  // ("Saving under <System> > <Sub-system>") so the user can confirm
  // the taxonomy slot before submitting.
  systemName: string
  // Set when the form was opened from the empty-state "Create new
  // asset" CTA — the dialog passes the typed search query through
  // so the form can pre-fill its Name field. Undefined for the
  // result-row "Can't find it?" flow where the user types the name
  // fresh.
  initialName?: string
  onCreated: (assetTypeId: string) => void
  onCancel: () => void
}

export interface AddAssetDialogProps {
  open: boolean
  onClose: () => void
  sessionId: string
  // null → site-level / pseudo-space capture. The capture-client
  // adapter (C2.7) accepts null and routes to POST /capture/items
  // unchanged.
  spaceId: string | null
  // Display name of the capture space (e.g. "Bedroom 1") so the
  // dialog header can echo back which space the new asset will
  // attach to. Optional — when omitted (e.g. site-level capture)
  // the sub-line falls back to a generic phrase.
  spaceName?: string | null
  excludedAssetTypeIds: ReadonlySet<string>
  captureClient: CaptureClient
  // Override for the dispatch fired when the user picks an asset type
  // from the search results. The default path dispatches `createItem`
  // through `captureClient`. Callers that need a different shape — for
  // example the room-capture flow, which has to dispatch `updateItem`
  // when the picked asset is already present but hidden via
  // `isZeroConfirmed = true` — provide this hook. Resolution closes the
  // dialog; rejection surfaces in the dialog's own error slot.
  onSelectAssetType?: (assetTypeId: string) => Promise<void>
  renderCreateVariantForm?: (props: CreateVariantFormRenderProps) => ReactNode
  // Test seam; production wires `defaultSearch`, which routes to the
  // local SQLite mirror on PowerSync builds and REST otherwise.
  search?: (
    query: string,
    init: { signal: AbortSignal },
  ) => Promise<AssetTypeSearchResponse>
  // Test seams for the SubSystemPicker step (no-results → create
  // flow). Production lets the picker default to its own
  // classification fetchers.
  loadSystems?: (init: { signal: AbortSignal }) => Promise<AssetSystemListRow[]>
  loadSubSystems?: (
    systemId: string,
    init: { signal: AbortSignal },
  ) => Promise<AssetSubSystemListRow[]>
}

interface VariantPick {
  subSystemId: string
  subSystemName: string
  systemName: string
}

export function AddAssetDialog({
  open,
  onClose,
  sessionId,
  spaceId,
  spaceName,
  excludedAssetTypeIds,
  captureClient,
  onSelectAssetType,
  renderCreateVariantForm,
  search = defaultSearch,
  loadSystems,
  loadSubSystems,
}: AddAssetDialogProps): ReactElement | null {
  const [inputValue, setInputValue] = useState('')
  const [searchState, setSearchState] = useState<SearchState>(IDLE_STATE)
  const [creatingAssetTypeId, setCreatingAssetTypeId] = useState<string | null>(
    null,
  )
  const [createError, setCreateError] = useState<string | null>(null)
  const [variantPick, setVariantPick] = useState<VariantPick | null>(null)
  // Set when the user enters the create flow from the empty-state
  // CTA (no search matches). Drives both the SubSystemPicker step
  // and the initialName passed into the variant form. Cleared on
  // cancel/close.
  const [pendingCreateName, setPendingCreateName] = useState<string | null>(
    null,
  )

  const resetAndClose = useCallback(() => {
    setInputValue('')
    setSearchState(IDLE_STATE)
    setCreatingAssetTypeId(null)
    setCreateError(null)
    setVariantPick(null)
    setPendingCreateName(null)
    onClose()
  }, [onClose])

  useAbortableEffect(
    (signal) => {
      if (!open) return undefined
      const trimmed = inputValue.trim()
      if (trimmed.length < ASSET_TYPE_SEARCH_MIN_QUERY) {
        setSearchState({
          status: 'idle',
          query: trimmed,
          results: [],
          errorMessage: null,
        })
        return undefined
      }
      setSearchState((prev) => ({
        status: 'loading',
        query: trimmed,
        results: prev.results,
        errorMessage: null,
      }))
      const timer = setTimeout(() => {
        search(trimmed, { signal })
          .then((response) => {
            setSearchState({
              status: 'ready',
              query: trimmed,
              results: response.results,
              errorMessage: null,
            })
          })
          .catch((err: unknown) => {
            if (isAbortError(err)) return
            setSearchState({
              status: 'error',
              query: trimmed,
              results: [],
              errorMessage: formatError(err),
            })
          })
      }, SEARCH_DEBOUNCE_MS)
      return () => {
        clearTimeout(timer)
      }
    },
    [open, inputValue, search],
  )

  const dispatchCreateItem = useCallback(
    async (assetTypeId: string) => {
      setCreatingAssetTypeId(assetTypeId)
      setCreateError(null)
      try {
        if (onSelectAssetType !== undefined) {
          await onSelectAssetType(assetTypeId)
        } else {
          await captureClient.dispatch({
            kind: 'createItem',
            sessionId,
            spaceId,
            assetTypeId,
            quantity: '0',
            isZeroConfirmed: false,
            notes: null,
          })
        }
        resetAndClose()
      } catch (err) {
        setCreateError(formatError(err))
        setCreatingAssetTypeId(null)
      }
    },
    [captureClient, onSelectAssetType, resetAndClose, sessionId, spaceId],
  )

  if (!open) return null

  const visibleResults = searchState.results.filter(
    (row) => !excludedAssetTypeIds.has(row.id),
  )
  const excludedCount = searchState.results.length - visibleResults.length
  const canCreateVariant = renderCreateVariantForm !== undefined
  // Token-prefix priority: rows whose name has a token starting with
  // the query render first; substring-only fallback renders under a
  // "Did you mean…?" header only when primary is empty. Backed by the
  // pure `classifyByQuery` helper so the rule is unit-testable.
  const { primary, secondary } = classifyByQuery(
    visibleResults,
    searchState.query,
  )
  const primaryGroups = groupBySystem(primary)
  const secondaryGroups = groupBySystem(secondary)
  const visibleCount =
    primary.length > 0 ? primary.length : secondary.length

  // Three-state render: form (variant picked) > picker (create-from-
  // empty-state pending) > search (default). The picker only ever
  // runs through the empty-state CTA path; the result-row "Can't
  // find it?" link jumps straight to `variantPick` so the form
  // opens immediately.
  const showVariantForm =
    variantPick !== null && renderCreateVariantForm !== undefined
  const showSubSystemPicker =
    !showVariantForm && pendingCreateName !== null && canCreateVariant

  // Header sub-line: echo the space name when we have one so the
  // user can verify they are about to file an asset under the
  // intended room.
  const subLine =
    spaceName !== undefined && spaceName !== null && spaceName.length > 0 ? (
      <>
        Find an asset type to add to{' '}
        <span className="font-medium text-cc-forest">{spaceName}</span>
      </>
    ) : (
      'Find an asset type to add to this space.'
    )

  return (
    <Dialog
      open={open}
      onClose={resetAndClose}
      title="Add asset"
      variant="bare"
      overlayClassName="fixed inset-0 z-40 bg-cc-forest/55 backdrop-blur-[2px]"
    >
      {/*
        max-h: dvh (dynamic viewport) instead of vh because iOS Safari and
        Android Chrome compute `100vh` against the LARGE viewport (URL bar
        hidden), so `max-h-[90vh]` ended up taller than the visible area
        when the URL bar was on screen — pushing the bottom of the modal
        (and on tall results, the search bar at the top of the scroll
        column) below the fold. dvh tracks the actual visible viewport
        and resizes when the URL bar shows/hides. Falls back to vh on
        engines without dvh support (none currently in our supported set).
      */}
      <div className="pointer-events-auto flex max-h-[90dvh] w-full max-w-[520px] flex-col overflow-hidden rounded-[20px] bg-white shadow-[0_30px_80px_-20px_rgba(16,49,43,0.45)]">
        <DialogHeader
          title="Add asset"
          subLine={subLine}
          onClose={resetAndClose}
        />

        {createError !== null ? (
          // Render at the dialog level so failures from the
          // variant-create → add-to-room hand-off surface in
          // form/picker mode too — not only in the search panel.
          // Without this, a 500 or offline failure during the
          // post-create dispatch would leave the form looking idle
          // with no visible error and the user would silently re-
          // submit (the backend is idempotent so the retry
          // succeeds, but the user has no way to know).
          <div className="px-6 pt-4">
            <Alert variant="error" title="Could not add asset">
              {createError}
            </Alert>
          </div>
        ) : null}

        {showVariantForm && variantPick !== null && renderCreateVariantForm ? (
          <div className="flex-1 overflow-y-auto">
            {renderCreateVariantForm({
              subSystemId: variantPick.subSystemId,
              subSystemName: variantPick.subSystemName,
              systemName: variantPick.systemName,
              initialName: pendingCreateName ?? undefined,
              onCreated: (assetTypeId) => {
                void dispatchCreateItem(assetTypeId)
              },
              onCancel: () => {
                setVariantPick(null)
                setPendingCreateName(null)
              },
            })}
          </div>
        ) : showSubSystemPicker && pendingCreateName !== null ? (
          <div className="flex-1 overflow-y-auto px-6 pt-4 pb-6">
            <SubSystemPicker
              assetName={pendingCreateName}
              onPick={(pick: SubSystemPick) => {
                setVariantPick({
                  subSystemId: pick.subSystemId,
                  subSystemName: pick.subSystemName,
                  systemName: pick.systemName,
                })
                setCreateError(null)
              }}
              onCancel={() => setPendingCreateName(null)}
              loadSystems={loadSystems}
              loadSubSystems={loadSubSystems}
            />
          </div>
        ) : (
          <AddAssetSearchResults
            inputValue={inputValue}
            onInputChange={setInputValue}
            searchState={searchState}
            visibleResults={visibleResults}
            excludedCount={excludedCount}
            primaryGroups={primaryGroups}
            secondaryGroups={secondaryGroups}
            visibleCount={visibleCount}
            creatingAssetTypeId={creatingAssetTypeId}
            canCreateVariant={canCreateVariant}
            onSelectExisting={(row) => {
              void dispatchCreateItem(row.id)
            }}
            onCreateFromQuery={(name) => {
              // Both entry points (empty-state CTA + result-row
              // footer pill) land here. We deliberately do NOT
              // auto-anchor to the first search result's sub-system
              // — that classification was almost always wrong for
              // the variant the surveyor wanted to file. The
              // SubSystemPicker step lets the user choose the
              // parent sub-system explicitly (PRD §6.5) before the
              // variant form opens.
              setPendingCreateName(name)
              setCreateError(null)
            }}
          />
        )}
      </div>
    </Dialog>
  )
}

interface DialogHeaderProps {
  title: string
  subLine: ReactNode
  onClose: () => void
}

function DialogHeader({
  title,
  subLine,
  onClose,
}: DialogHeaderProps): ReactElement {
  return (
    <header className="flex items-start justify-between gap-4 border-b border-cc-forest/10 px-6 pt-6 pb-4">
      <div className="min-w-0">
        <p
          aria-hidden="true"
          className="font-display text-[26px] leading-none text-cc-forest"
        >
          {title}
        </p>
        <p className="mt-1.5 text-[13px] text-cc-forest/60">{subLine}</p>
      </div>
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="-mr-1 -mt-1 flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full text-cc-forest/70 transition-colors hover:bg-cc-cream hover:text-cc-forest focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cc-forest"
      >
        <svg
          aria-hidden="true"
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
        >
          <path
            d="M2 2 L12 12 M12 2 L2 12"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
          />
        </svg>
      </button>
    </header>
  )
}

const IDLE_STATE: SearchState = {
  status: 'idle',
  query: '',
  results: [],
  errorMessage: null,
}

function formatError(err: unknown): string {
  if (err instanceof ApiError) return err.message
  if (err instanceof Error) return err.message
  return 'Unexpected error'
}
